export type ActionFit = {
  /** The number of actions in the "more" menu. */
  overflowCount: number;
  /** Row widths that did not fit, by overflow count: the room that a smaller overflow count needs. */
  requiredWidths: number[];
  /** The row width when the overflow count last changed. Until the row is measured again, it is stale. */
  decidedAtRowWidth: number | null;
};

// A margin in pixels on both sides of the fitting width, so sub-pixel changes cannot move an action
// back and forth.
const FIT_TOLERANCE_PX = 1;

/**
 * Decides how many header actions move into the "more" menu so the row fits the available width.
 * Returns the same object when nothing changes, so the caller can compare it while rendering.
 */
export function getNextActionFit(
  fit: ActionFit,
  rowWidth: number | null,
  availableWidth: number | null,
  movableCount: number
): ActionFit {
  if (rowWidth === null || availableWidth === null) {
    return fit;
  }

  const previousRequiredWidth = fit.requiredWidths[fit.overflowCount - 1];
  if (fit.overflowCount > 0 && availableWidth >= previousRequiredWidth + FIT_TOLERANCE_PX) {
    return {
      overflowCount: fit.overflowCount - 1,
      requiredWidths: fit.requiredWidths.slice(0, -1),
      decidedAtRowWidth: rowWidth,
    };
  }

  const isRowMeasuredSinceDecision = rowWidth !== fit.decidedAtRowWidth;
  if (
    rowWidth > availableWidth + FIT_TOLERANCE_PX &&
    isRowMeasuredSinceDecision &&
    fit.overflowCount < movableCount
  ) {
    return {
      overflowCount: fit.overflowCount + 1,
      requiredWidths: [...fit.requiredWidths, rowWidth],
      decidedAtRowWidth: rowWidth,
    };
  }

  return fit;
}
